import React from 'react';
import PropTypes from 'prop-types';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    SortingState,
    ColumnSort,
} from '@tanstack/react-table';

import './FlightTable.scss';
import { FlightTableRow } from 'models/flightTableRow';
import { convertCamelCaseToSkewerCase, formatTimeDifference } from 'utils/format';
import { getAlertClasses } from 'utils/alerts';
import { Dashboard } from 'models/dashboard';
import { CodeFormat } from 'models/codeFormat';

interface FlightTableProps {
    rows: FlightTableRow[];
    dashboard: Dashboard;
}

const columnHelper = createColumnHelper<FlightTableRow>();

const getColumns = (codeFormat: string) => [
    columnHelper.accessor((row) => row.ident[codeFormat as keyof CodeFormat], {
        id: 'ident',
        header: 'Ident',
        cell: (props) => (
            <>
                <img
                    className="flight__logo"
                    src={props.row.original.airlineIconUrl}
                    alt={`Logo for airline ${props.row.original.operator.icao}`}
                    onError={({ currentTarget }) => {
                        // Handle case where no image exists
                        currentTarget.src = '/airplane.png';
                    }}
                />
                {props.getValue()}
            </>
        ),
    }),
    columnHelper.accessor('registration', {
        header: 'Registration',
    }),
    columnHelper.accessor((row) => row.origin[codeFormat as keyof CodeFormat], {
        id: 'origin',
        header: 'Origin',
    }),
    columnHelper.accessor((row) => row.destination[codeFormat as keyof CodeFormat], {
        id: 'destination',
        header: 'Destination',
    }),
    columnHelper.accessor('estimatedOnUnixTime', {
        id: 'estimatedOn',
        header: 'Current EON',
        cell: (props) => props.row.original.estimatedOn,
        sortUndefined: 1,
    }),
    columnHelper.accessor('predictedOnUnixTime', {
        id: 'predictedOn',
        header: 'Predicted ON',
        cell: (props) => props.row.original.predictedOn,
        sortUndefined: 1,
    }),
    columnHelper.accessor('predictedOnDifference', {
        header: 'Difference',
        cell: (props) => formatTimeDifference(props.getValue()),
        sortUndefined: 1,
    }),
    columnHelper.accessor('estimatedInUnixTime', {
        id: 'estimatedIn',
        header: 'Current EIN',
        cell: (props) => props.row.original.estimatedIn,
        sortUndefined: 1,
    }),
    columnHelper.accessor('predictedInUnixTime', {
        id: 'predictedIn',
        header: 'Predicted IN',
        cell: (props) => props.row.original.predictedIn,
        sortUndefined: 1,
    }),
    columnHelper.accessor('predictedInDifference', {
        header: 'Difference',
        cell: (props) => formatTimeDifference(props.getValue()),
        sortUndefined: 1,
    }),
];

export default function FlightTable(props: FlightTableProps): JSX.Element {
    const initialSorting: ColumnSort[] = [];
    const [sortByColumn, sortOrder] = (props.dashboard.sortBy || '').split('-');
    if (sortByColumn) {
        initialSorting.push({ id: sortByColumn, desc: sortOrder === 'desc' });
    }

    const [sorting, setSorting] = React.useState<SortingState>(initialSorting);

    const table = useReactTable({
        data: props.rows,
        columns: getColumns(props.dashboard.operatorDisplayFormat),
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <table className="flight-table">
            <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <th
                                className={
                                    'flight-table__heading flight-table__heading--' +
                                    convertCamelCaseToSkewerCase(header.id) +
                                    (header.column.getCanSort()
                                        ? ' flight-table__heading--sorted-' + (header.column.getIsSorted() as string)
                                        : '')
                                }
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                            >
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map((row) => (
                    <tr
                        className={
                            'flight ' +
                            getAlertClasses(
                                row.original.estimatedOnUnixTime,
                                row.original.predictedOnUnixTime,
                                row.original.estimatedInUnixTime,
                                row.original.predictedInUnixTime,
                                props.dashboard.alerts.thresholdEarly,
                                props.dashboard.alerts.thresholdLate
                            )
                        }
                        key={row.id}
                    >
                        {row.getVisibleCells().map((cell) => (
                            <td
                                className={'flight__cell flight__cell--' + convertCamelCaseToSkewerCase(cell.column.id)}
                                key={cell.id}
                            >
                                <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

FlightTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object),
};
